/**
 *
 * FBAppointment
 *
 */

import React, { memo, useCallback, useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

// styles
import './style.scss';

// antd component
import { Form, Dropdown, Button } from 'antd';

// custom react-multi-date-picker
import DatePicker from 'react-multi-date-picker';
import TimePicker from 'react-multi-date-picker/plugins/time_picker';
import { months, weekDays } from 'utils/moment/multiDateLocale';

// icons
import CustomIcons from 'components/CustomIcons';

// layout components
import ErrorBoundary from 'engine/ErrorBoundary';

import {
  FBSectionHeader,
  FBSectionErroMessage,
  FBSectionFieldActions,
} from 'components/Communications';

import HiddenField from 'components/Forms/Fields/HiddenField';
// inner components
import SectionFields from './components/SectionFields';

// hooks
import { useRefState } from 'utils/hooks';

// custom react-multi-date-picker
import { DateObject } from 'react-multi-date-picker';
import { timeFormat, dateFormat } from 'utils/moment/moment.constant';

function FBAppointment({
  name,
  mode,
  method,
  initialValues,
  isValidSection,
  // actions
  setValidSection,
  onDelete,
  onChangeSection,
  // default props
  className,
  ...rest
}) {
  const childClassNames = classNames(
    'tcf-fb-appointments-group',
    'tcf-fb-group',
    className,
  );

  let formName = name;

  const [dynamicAppointment] = Form.useForm();

  const [validFields, setValidFields] = useState(isValidSection || false);
  const [currentValues, currentValuesRef, setCurrentValues] = useRefState(
    initialValues || {},
  );

  const formatDateTimeValue = useCallback((value, format) => {
    if (!!!value) {
      return '';
    }

    if (typeof value == 'object') {
      return !!value && value.format(format);
    } else {
      return !!value && new DateObject(value).format(format);
    }

  }, []);

  function validateForm(formData, name = 'appointments') {
    let isValid = true;
    if (Array.isArray(formData?.[`${name}-fields`]) && formData?.[`${name}-fields`]?.length > 0) {
      for (let field of formData?.[`${name}-fields`]) {
        if (!field?.['field-date']) {
          isValid = false;
          break;
        }
    
        if (field?.['field-type'] === 'radio' || field?.['field-type'] === 'checkbox') {
          if (!field?.['field-input'] || field?.['field-input']?.some(input => !input?.['time-from'] || !input?.['time-to'])) {
            isValid = false;
            break;
          }
        }
      }
    } else {
      isValid = false;
    }
    return isValid;
  }

  useEffect(() => {
    if (!!initialValues) {
      const isValid = validateForm(initialValues);
      setValidFields(isValid);
      !!setValidSection && setValidSection(name, isValid);
      if (JSON.stringify(initialValues) != JSON.stringify(currentValues)) {
        setCurrentValues(initialValues);
      }
    } else if (!!!initialValues) {
      resetAllStates();
    }

    return () => {
      resetAllStates();
    };
  }, []);

  const resetAllStates = useCallback(() => {
    setCurrentValues({});
    setValidFields(false);
    dynamicAppointment.resetFields();
  }, [initialValues, currentValues]);

  // Delete Section
  const deleteSection = useCallback(name => {
    resetAllStates();
    onChangeSectionHandler(null);
    !!onDelete && onDelete(name);
  }, []);

  // update Section
  const onChangeSectionHandler = useCallback(
    sectionData => {
      !!onChangeSection && onChangeSection(name, sectionData);
    },
    [currentValues, onChangeSection],
  );

  // custom Validation for Section fileds
  const checkValidCallback = useCallback(
    async (rule, values, callback) => {
      const isValid = validateForm(currentValuesRef?.current);
      setValidFields(isValid);
      !!setValidSection && setValidSection(name, isValid);
      
      if (isValid) {
        return Promise.resolve(isValid);
      } else {
        return Promise.reject(
          new Error("L'entrée n'est pas valide."),
          values,
        );
      }
    },
    [setValidSection, currentValuesRef],
  );

  const erroMessage = useMemo(
    () => (
      <FBSectionErroMessage
        showMessage={!validFields}
        message="Cette section nécessite plusieurs champs à remplir."
      />
    ),
    [validFields, isValidSection],
  );

  // Update parent states based on changes inside Form
  const handleValuesChange = useCallback(
    (changedValues, allValues) => {
      let values = allValues[name] || {};
      const isValid = validateForm(values);
      setValidFields(isValid);
      !!setValidSection && setValidSection(name, isValid);
      !!isValid && setCurrentValues(values);
    //  dynamicAppointment.validateFields();
    },
    [name, currentValues, setValidSection],
  );

  /* Generate Filed based on chozen type */
  const generateRadioOrCheckboxFiled = useCallback((element, fieldType) => {
    const { key, name, fieldKey, ...restField } = element;
    return (
      <>
        <Form.List
          {...restField}
          name={[name, 'field-input']}
          fieldKey={[fieldKey, 'field-input']}
          className={`appointment-field ${fieldType}`}
          rules={[
            {
              required: true,
              validator: async (_, vals) => {
                if (!!!vals) {
                  return Promise.reject(new Error('Champ obligatoire'));
                }
              },
              message: 'Champ obligatoire',
              validateTrigger: 'onChange',
            },
          ]}
        >
          {(fields, { add, remove, move }) => {
            return (
              <>
                {fields.map((element, index) => {
                  const { key, name, fieldKey, ...restField } = element;
                  return (
                    <div
                      key={key + index}
                      className={`appointment-row appointment-row-${index} ${fieldType}`}
                      align="baseline"
                    >
                      <div className="options-wrapper">
                        <Form.Item
                          {...restField}
                          name={[name, `time-from`]}
                          fieldKey={[fieldKey, `time-from`]}
                          className="appointment-field"
                          rules={[
                            {
                              required: true,
                              validator: async (_, vals) => {
                                if (!!!vals) {
                                  return Promise.reject(new Error('Champ obligatoire'));
                                }
                              },
                              message: 'Champ obligatoire',
                              validateTrigger: 'onChange',
                            },
                          ]}
                        >
                          <DatePicker
                            className="ant-form-item time"
                            scrollSensitive={false}
                            hideOnScroll
                            editable={false}
                            single
                            disableYearPicker
                            disableMonthPicker
                            hideMonth
                            hideYear
                            hideWeekDays
                            arrow={false}
                            rules={{ required: true }}
                            format="HH:mm"
                            plugins={[<TimePicker position="top" />]}
                            placeholder="Heure de début"
                          />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          name={[name, `time-to`]}
                          fieldKey={[fieldKey, `time-to`]}
                          className="appointment-field"
                          rules={[
                            {
                              required: true,
                              validator: async (_, vals) => {
                                if (!!!vals) {
                                  return Promise.reject(new Error('Champ obligatoire'));
                                }
                              },
                              message: 'Champ obligatoire',
                              validateTrigger: 'onChange',
                            },
                          ]}
                        >
                          <DatePicker
                            className="ant-form-item time"
                            scrollSensitive={false}
                            hideOnScroll
                            editable={false}
                            single
                            disableYearPicker
                            disableMonthPicker
                            hideMonth
                            hideYear
                            hideWeekDays
                            arrow={false}
                            rules={{ required: true }}
                            format="HH:mm"
                            plugins={[<TimePicker position="top" />]}
                            placeholder="Heure de début"
                          />
                        </Form.Item>
                      </div>
                      <div className="appointment-row__actions">
                        {fields?.length > 2 && (
                          <CustomIcons
                            className="dynamic-delete-button"
                            type="garbage"
                            onClick={() => remove(name)}
                          />
                        )}
                      </div>
                    </div>
                  );
                })}

                <Button className="add-sub-options" onClick={() => add()}>
                  Ajouter une option
                </Button>
              </>
            );
          }}
        </Form.List>
      </>
    );
  }, []);

  const generateFileldByType = useCallback(
    (element, index) => {
      const { key, name, fieldKey, ...restField } = element;
      let cur = dynamicAppointment.getFieldsValue();
      let curV =
        Object.keys(cur)?.length > 0 ? cur : { [formName]: currentValues };
      let filedsValues = curV[formName][`${formName}-fields`] || [];
      let rowValsType =
        filedsValues?.length > 0 && filedsValues[name]?.['field-type'];

      switch (rowValsType) {
        case 'radio':
          return generateRadioOrCheckboxFiled(element, 'radio');
        case 'checkbox':
          return generateRadioOrCheckboxFiled(element, 'checkbox');
        default:
          return <></>;
      }
    },
    [initialValues, currentValues],
  );

  /* Add new Filed Row */
  const fieldTypeList = [
    { label: 'Choix multiple', key: 'checkbox' },
    { label: 'Choix unique', key: 'radio' },
  ];
  const addNewTypeField = useCallback((type, addCalback) => {
    let defVals = {
      'field-date': null,
      'field-type': type,
      'field-input': null,
    };
    if (type == 'radio') {
      defVals['field-input'] = [
        { [`time-from`]: null,  [`time-to`]: null },
        { [`time-from`]: null,  [`time-to`]: null },
      ];
    } else if (type == 'checkbox') {
      defVals['field-input'] = [
        { [`time-from`]: null,  [`time-to`]: null },
        { [`time-from`]: null,  [`time-to`]: null },
      ];
    }
    setValidFields(false);
    !!setValidSection && setValidSection(name, false);
    addCalback(defVals);
  }, [setValidSection, name]);
  const dorpdownAddNewRow = useCallback(
    addCallback => (
      <div className="add-new-filed__wrapper">
        <Dropdown
          menu={{ items: fieldTypeList }}
          trigger={['hover']}
          className="add-new-filed"
          overlayClassName="add-new-filed__dropdown"
          getPopupContainer={trigger => trigger.parentElement}
          dropdownRender={menu => {
            const items = menu.props.items;
            return (
              <div className="dropdown-content">
                {items?.map(({ key, label }) => {
                  return (
                    <div
                      className="dropdown-content__item"
                      onClick={() => addNewTypeField(key, addCallback)}
                    >
                      {label}
                    </div>
                  );
                })}
              </div>
            );
          }}
          placement="top"
          arrow={{
            pointAtCenter: true,
          }}
        >
          <Button>
            <span className="add-new-filed__btn">Ajouter un jour</span>
          </Button>
        </Dropdown>
      </div>
    ),
    [addNewTypeField],
  );

  return (
    <div className={childClassNames}>
      <Form
        name={formName}
        form={dynamicAppointment}
        initialValues={{ [formName]: currentValues }}
        className="tcf-form-builder"
        autoComplete="off"
        onValuesChange={handleValuesChange}
      >
        <FBSectionHeader
          key="appointments-section-header"
          type="appointments"
          onDelete={() => deleteSection(formName)}
        />
        <div className="tcf-fb-group">
          <SectionFields key="appointments" name={formName} sectionValues={currentValues} />
          <ErrorBoundary>
            <Form.List
              className="tcf-fb-group"
              name={[formName, `${formName}-fields`]}
              rules={[
                {
                  required: true,
                  validator: checkValidCallback,
                  validateTrigger: 'onChange',
                  message: 'Champ obligatoire',
                },
              ]}
            >
              {(fields, { add, remove, move }) => {
                return (
                  <>
                    {fields.map((element, index) => {
                      const { key, name, fieldKey, ...restField } = element;
                      return (
                        <div
                          key={currentValues?.[`${formName}-fields`][name]?.['field-type']+`${name}`}
                          className="appointment-group"
                          align="baseline"
                        >
                          <FBSectionFieldActions
                            type="appointments"
                            key={`${formName}-section-action-${index}`}
                            index={name}
                            fieldType={
                              currentValues?.[`${formName}-fields`][name]?.['field-type']
                            }
                            fieldTypeList={fieldTypeList}
                            totalLength={
                              currentValues[`${formName}-fields`]?.length || 0
                            }
                            onMove={move}
                            onDelete={() => remove(name)}
                          />

                          <div className="appointment-group__context">
                            <HiddenField
                              key={[name, `field-type`]}
                              data={{ name: [name, `field-type`] }}
                              index={0}
                            />

                            <Form.Item
                              {...restField}
                              name={[name, 'field-date']}
                              fieldKey={[fieldKey, 'field-date']}
                              className="appointment-title"
                              rules={[
                                {
                                  required: true,
                                  message: 'Champ obligatoire',
                                },
                              ]}
                            >
                              <DatePicker
                                className="ant-form-item date"
                                rules={{ required: true }}
                                scrollSensitive={false}
                                hideOnScroll
                                editable={false}
                                format="DD.MM.YYYY"
                                months={months}
                                weekDays={weekDays}
                                placeholder="Date"
                              />
                            </Form.Item>

                            {generateFileldByType(element)}
                          </div>
                        </div>
                      );
                    })}

                    {dorpdownAddNewRow(add)}
                  </>
                );
              }}
            </Form.List>
          </ErrorBoundary>
          {erroMessage}
        </div>
      </Form>
    </div>
  );
}

FBAppointment.defaultProps = {
  name: 'appointments',
  mode: 'new',
  method: 'nouvel',
};
FBAppointment.propTypes = {
  onDelete: PropTypes.func
};

export default memo(FBAppointment);
